// Live bottle preview at true label proportions.
//
// Draws with exactly the same function as the downloaded PNG
// (`drawBottleComposite` in lib/label-canvas/render.ts) — only the scale
// differs (display px × devicePixelRatio instead of 2× the photo) — so what the
// visitor sees is what they download. Redraws are coalesced to one per
// animation frame. SSR renders an empty, correctly-sized box; the canvas is
// drawn after hydration.
import * as React from "react";
import { cn } from "@/lib/utils";
import type { Assets, BottleSpec } from "@/lib/label-canvas/bottles";
import { loadFonts } from "@/lib/label-canvas/fonts";
import { subscribeOrnaments } from "@/lib/label-canvas/ornaments";
import { bottleCompositeSize, drawBottleComposite } from "@/lib/label-canvas/render";
import type { CanvasData } from "@/lib/label-canvas/schema";

export interface BottlePreviewProps {
  doc: CanvasData;
  assets: Assets;
  bottle: BottleSpec;
  className?: string;
  /** Overrides the default description. */
  ariaLabel?: string;
}

const MAX_BACKING_PX = 4096;

export function BottlePreview({ doc, assets, bottle, className, ariaLabel }: BottlePreviewProps) {
  const boxRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [box, setBox] = React.useState<{ w: number; h: number } | null>(null);
  const [fontsTick, setFontsTick] = React.useState(0);
  const [ornamentsTick, setOrnamentsTick] = React.useState(0);

  const natural = bottleCompositeSize(bottle, 1);
  const aspect = natural.width / natural.height;

  // Track the container size.
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setBox({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Web fonts: redraw once the faces this design uses are ready, and whenever
  // any font finishes loading later.
  const faceKey = doc.elements
    .map((e) => (e.type === "text" ? `${e.font}/${e.weight}/${e.italic}` : ""))
    .join("|");
  React.useEffect(() => {
    let alive = true;
    loadFonts(doc).then(() => alive && setFontsTick((t) => t + 1));
    return () => {
      alive = false;
    };
    // faceKey captures the font-relevant part of doc; re-run only when that changes.
  }, [faceKey]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (typeof document === "undefined" || !("fonts" in document)) return;
    const onDone = () => setFontsTick((t) => t + 1);
    document.fonts.addEventListener("loadingdone", onDone);
    return () => document.fonts.removeEventListener("loadingdone", onDone);
  }, []);

  // Ornaments decode asynchronously: redraw as each lands.
  React.useEffect(() => subscribeOrnaments(() => setOrnamentsTick((t) => t + 1)), []);

  // Fit inside the container: fill its height, unless that would overflow its width.
  let cssW = 0;
  let cssH = 0;
  if (box && box.w > 0 && box.h > 0) {
    cssH = box.h;
    cssW = cssH * aspect;
    if (cssW > box.w) {
      cssW = box.w;
      cssH = cssW / aspect;
    }
  }

  // Draw, at most once per frame.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || cssW <= 0 || cssH <= 0) return;
    const frame = requestAnimationFrame(() => {
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      let bw = Math.round(cssW * dpr);
      if (bw > MAX_BACKING_PX) bw = MAX_BACKING_PX;
      const scale = bw / natural.width;
      const size = bottleCompositeSize(bottle, scale);
      if (canvas.width !== size.width) canvas.width = size.width;
      if (canvas.height !== size.height) canvas.height = size.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawBottleComposite(ctx, doc, assets, bottle, scale);
    });
    return () => cancelAnimationFrame(frame);
  }, [doc, assets, bottle, cssW, cssH, natural.width, fontsTick, ornamentsTick]);

  const label =
    ariaLabel ??
    `${doc.bottle === "mcc" ? "Sparkling (MCC)" : "Wine"} bottle showing your ${doc.label.width_mm} × ${doc.label.height_mm} mm front label at true proportions`;

  return (
    <div ref={boxRef} className={cn("relative flex items-center justify-center", className)}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        className="block"
        style={
          cssW > 0
            ? { width: cssW, height: cssH }
            : { height: "100%", aspectRatio: `${natural.width} / ${natural.height}` }
        }
      />
    </div>
  );
}

export default BottlePreview;
